import { useEffect, useState } from 'react';

interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

interface TableOfContentsProps {
  containerRef: React.RefObject<HTMLElement | null>;
  /** Resets observer after content changes — pass the rendered role/content key. */
  contentKey: string;
}

export function TableOfContents({ containerRef, contentKey }: TableOfContentsProps) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const headings = Array.from(
      container.querySelectorAll<HTMLHeadingElement>('h2[id], h3[id]'),
    );
    setEntries(
      headings.map((h) => ({
        id: h.id,
        text: h.textContent ?? '',
        level: h.tagName === 'H2' ? 2 : 3,
      })),
    );
  }, [containerRef, contentKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const headings = Array.from(
      container.querySelectorAll<HTMLHeadingElement>('h2[id], h3[id]'),
    );
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (items) => {
        const visible = items
          .filter((i) => i.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [containerRef, contentKey, entries.length]);

  if (entries.length === 0) return null;

  const list = (
    <ol className="space-y-1 text-sm">
      {entries.map((e) => (
        <li key={e.id} className={e.level === 3 ? 'pl-4' : ''}>
          <a
            href={`#${e.id}`}
            aria-current={e.id === activeId ? 'location' : undefined}
            className={`block py-1 transition-colors ${
              e.id === activeId
                ? 'text-action font-semibold'
                : 'text-wolf-400 hover:text-wolf-100'
            }`}
          >
            {e.text}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <>
      <details className="md:hidden mb-6 border border-wolf-700 rounded p-3">
        <summary className="cursor-pointer text-sm font-semibold text-wolf-100">
          Jump to…
        </summary>
        <nav aria-label="On this page" className="mt-2">
          {list}
        </nav>
      </details>
      <nav
        aria-label="On this page"
        className="hidden md:block sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto"
      >
        {list}
      </nav>
    </>
  );
}
